/**
 * PipelineProgress — live step-by-step progress during chat inference.
 *
 * Shows each pipeline step as it runs/completes. Completed steps fade
 * out sequentially after a delay, with the remaining steps sliding up
 * smoothly. During inference, shows a client-side elapsed timer and live
 * token count. The narrative ticker rides underneath (InferenceNarrative) —
 * call sites with a streaming draft render it below that instead, so it
 * stays at the bottom of the feedback.
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import type { ProgressStep, InferenceProgress, InferenceMeta } from '@state/chat';
import { getStageLabel } from '@skjv/common';
import { LoadingSpinner, CheckIcon } from '@components/icons';
import { formatDuration } from './format-duration';
import { InferenceNarrative } from './inference-narrative';

interface PipelineProgressProps {
  steps: ProgressStep[];
  inferenceProgress?: InferenceProgress;
  inferenceMeta?: InferenceMeta;
  /** False when the call site renders the narrative itself, below a draft. */
  showNarrative?: boolean;
}

export function PipelineProgress({
  steps,
  inferenceProgress,
  inferenceMeta,
  showNarrative = true,
}: PipelineProgressProps) {
  const hasRunningStep = steps.some((s) => s.status === 'running');
  const isInferring = steps.some((s) => s.step === 'reasoning' && s.status === 'running');
  const fadedSteps = useFadeQueue(steps);
  const elapsedSec = useElapsedTimer(isInferring);

  const tokenCount = inferenceProgress?.tokenCount ?? 0;
  const maxTokens = inferenceMeta?.maxTokens ?? 0;

  return (
    <div className="pipeline-progress">
      {steps.map((step) => (
        <div
          key={step.step}
          className={`pipeline-step pipeline-step-${step.status} ${fadedSteps.has(step.step) ? 'pipeline-step-faded' : ''}`}
        >
          {step.status === 'running'
            ? <LoadingSpinner className="h-3 w-3 shrink-0" />
            : <CheckIcon className="h-3 w-3 shrink-0 pipeline-step-check" />
          }
          <span>{getStageLabel(step.step)}</span>
          {step.status === 'done' && step.durationMs != null && (
            <span className="pipeline-step-duration">{formatDuration(step.durationMs)}</span>
          )}
        </div>
      ))}
      {isInferring && elapsedSec > 0 && (
        <div className="pipeline-token-counter">
          <span>
            {tokenCount > 0
              ? `${tokenCount} / ${maxTokens} tokens`
              : 'Generating response...'}
          </span>
          <span className="pipeline-token-sep">|</span>
          <span>{formatElapsed(elapsedSec)} elapsed</span>
        </div>
      )}
      {showNarrative && (
        <InferenceNarrative
          active={hasRunningStep}
          meta={inferenceMeta ? { ...inferenceMeta } : undefined}
        />
      )}
    </div>
  );
}

/** Format seconds into "45 sec" or "3 min 45 sec". */
function formatElapsed(totalSec: number): string {
  const min = Math.floor(totalSec / 60);
  const sec = totalSec % 60;
  if (min === 0) return `${sec} sec`;
  return `${min} min ${sec} sec`;
}

/** Client-side timer that ticks every second while active. */
function useElapsedTimer(active: boolean): number {
  const [elapsed, setElapsed] = useState(0);
  const startRef = useRef<number | null>(null);

  useEffect(() => {
    if (!active) {
      setElapsed(0);
      startRef.current = null;
      return;
    }
    if (!startRef.current) startRef.current = Date.now();
    const interval = setInterval(() => {
      setElapsed(Math.floor((Date.now() - startRef.current!) / 1000));
    }, 1000);
    return () => clearInterval(interval);
  }, [active]);

  return elapsed;
}

/** Fade completed steps once a newer step starts running. */
function useFadeQueue(steps: ProgressStep[]): Set<string> {
  const [faded, setFaded] = useState<Set<string>>(new Set());
  const timersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  const scheduleRef = useRef<(step: string, delayMs: number) => void>();
  scheduleRef.current = (step: string, delayMs: number) => {
    if (timersRef.current.has(step)) return;
    const id = setTimeout(() => {
      setFaded((prev) => new Set(prev).add(step));
      timersRef.current.delete(step);
    }, delayMs);
    timersRef.current.set(step, id);
  };

  const schedule = useCallback((step: string, delayMs: number) => {
    scheduleRef.current?.(step, delayMs);
  }, []);

  useEffect(() => {
    const hasRunning = steps.some((s) => s.status === 'running');
    if (!hasRunning) return;

    const doneSteps = steps.filter((s) => s.status === 'done');
    doneSteps.forEach((s, i) => {
      if (!faded.has(s.step)) {
        schedule(s.step, 1500 + i * 600);
      }
    });
  }, [steps, faded, schedule]);

  useEffect(() => {
    const timers = timersRef.current;
    return () => { timers.forEach(clearTimeout); };
  }, []);

  return faded;
}
